import { Component, computed, Inject, input, output } from '@angular/core';

import { provideCurrentUserPreferencesContract } from '@core/account-contracts';
import { CurrentUserPreferencesFacade } from '@core/account-logic';
import { APP_CONFIG } from '@core/common-legacy';
import { ThemeSelectTemplateComponent } from '@core/components-theme-picker-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { CompanyVerificationInfoboxComponent } from '@core/tellent-company-verification-smart-ui';
import type {
  Org,
  TellentCurrentUser,
  TellentIAppConfig,
  TellentMember,
} from '@core/tellent-types';
import { resolveMemberFullName, resolveMemberInitials } from '@core/tellent-util';
import type { ThemeName } from '@core/theme-util';
import {
  AvatarComponent,
  DividerComponent,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconComponent,
  IconName,
  NestedTemplateTriggerDirective,
  TruncateComponent,
} from '@core/user-interface-legacy';

import { ImpactTrackerInsightsDropdownComponent } from '../../impact-tracker/impact-tracker-insights-dropdown/impact-tracker-insights-dropdown.component';
import { CompanySelectTemplateComponent } from '../company-select-template/company-select-template.component';
import { AdminDropdownAnalyticsService } from '../tellent-profile-dropdown.analytics';
import { injectTellentProfileDropdownConfig } from '../tellent-profile-dropdown.config';

@Component({
  selector: 'tla-admin-profile-dropdown',
  imports: [
    AvatarComponent,
    CompanySelectTemplateComponent,
    DividerComponent,
    DropdownComponent,
    DropdownItemComponent,
    TruncateComponent,
    IconComponent,
    NestedTemplateTriggerDirective,
    CompanySelectTemplateComponent,
    TranslatePipe,
    ThemeSelectTemplateComponent,
    CompanyVerificationInfoboxComponent,
    ImpactTrackerInsightsDropdownComponent,
  ],
  providers: [provideCurrentUserPreferencesContract({ useExisting: CurrentUserPreferencesFacade })],
  templateUrl: './admin-profile-dropdown.component.html',
  styleUrl: './admin-profile-dropdown.component.less',
})
export class AdminProfileDropdownComponent {
  protected readonly DropdownWidth = DropdownWidth;

  public config = injectTellentProfileDropdownConfig();

  public currentMember = input.required<TellentMember | undefined>();
  public currentCompany = input.required<Org | undefined>();
  public currentUser = input.required<TellentCurrentUser | null>();
  public companies = input.required<TellentMember[]>();
  public hasThemeSwitcher = input<boolean>(false);
  public themeName = input<ThemeName>();
  public hasAmbassadorProgram = input<boolean>(false);
  public hasImpactTrackerInsights = input<boolean>(false);

  public themeChange = output<ThemeName>();

  public avatarThumbUrl = computed(() => this.currentUser()!.avatarThumbUrl);

  public fullName = computed(() => {
    const { firstName, lastName, email } = this.currentUser()!;

    return resolveMemberFullName({ firstName, lastName, email });
  });

  public initials = computed(() => {
    const { firstName, lastName, email } = this.currentUser()!;
    return resolveMemberInitials({ firstName, lastName, email });
  });

  public themeIconRecord: Record<ThemeName, IconName> = {
    light: 'light',
    dark: 'dark',
    auto: 'system-preferences',
  };
  public themeNameRecord: Record<ThemeName, string> = {
    light: 'app.header.appearance.light',
    dark: 'app.header.appearance.dark',
    auto: 'app.header.appearance.system',
  };

  constructor(
    private analytics: AdminDropdownAnalyticsService,
    @Inject(APP_CONFIG) public appConfig: TellentIAppConfig,
  ) {}

  public signOut() {
    this.analytics.trackLogoutSelected();
    window.location.replace(`${this.appConfig.tellentAuthUrl}/sign-out`);
  }
  public onEditProfileClicked() {
    this.analytics.trackProfileSettingsSelected();
    this.config.handlers.editProfile('admin_dropdown');
  }

  public onCompanySelectClicked() {
    this.analytics.trackCompanySwitcherSelected();
  }
}
